// The « Nouveautés » pop-up (owner, 2026-10-08). Opens once per release for
// each admin browser (lib/patchNotes.ts), one feature per screen: what changes,
// how to use it, and a picture of where to look. Reopened from the sidebar.
//
// The pictures are small live replicas of the real controls (same classes), so
// they stay sharp in light and night mode; an item with `image` shows that
// screenshot instead.
import { useEffect, useState, type ReactNode } from 'react';
import { useT } from '@/lib/i18n';
import { PATCH_NOTES, unseenRelease, markPatchSeen, type PatchRelease, type PatchVisual } from '@/lib/patchNotes';
import { RouteMap } from '@/pages/admin/delivery/RouteMap';

const bubbleUs = 'rounded-2xl rounded-tr-sm bg-brand-50 dark:bg-brand-900/30 border border-brand-200/70 px-3 py-2 text-[12px] leading-snug text-gray-800 ml-auto max-w-[85%] whitespace-pre-line';
const bubbleHer = 'rounded-2xl rounded-tl-sm bg-gray-100 px-3 py-2 text-[12px] leading-snug text-gray-800 max-w-[85%]';
const primaryBtn = 'inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-brand-500 text-white text-[12px] font-semibold';

function Visual({ kind }: { kind: PatchVisual }) {
  const { t } = useT();
  switch (kind) {
    case 'preview-approval':
      return (
        <div className="space-y-2">
          <p className="text-[10.5px] text-gray-400 text-center">{t('WhatsApp — votre numéro')}</p>
          <div className={`${bubbleHer} whitespace-pre-line`}>
            {'👀 PREVIEW TO APPROVE — #1205\nSara · 40 photos · 36 pages (Solo)\n✓ Send order kept · ✓ no cut faces\n3D preview: app.storiz.ma/preview/…\nReply OK to send (or OK 1205).'}
          </div>
          <div className={`${bubbleUs} w-fit`}>OK</div>
          <div className="flex items-center gap-2 pt-1">
            <span className="text-[11px] text-gray-500">{t('ou dans l’Inbox :')}</span>
            <span className={primaryBtn}>✓ {t('Approuver et envoyer')}</span>
          </div>
        </div>
      );
    case 'address-ask':
      return (
        <div className="space-y-2">
          <div className={bubbleHer}>{t('C’est parfait, merci ! 😍')}</div>
          <div className={bubbleUs}>{t('Pour la livraison, envoyez-nous votre nom complet, votre ville et votre adresse complète (quartier, rue, numéro).')}</div>
          <p className="text-[10.5px] text-gray-400 text-right">{t('envoyé automatiquement · 2 fois au plus')}</p>
        </div>
      );
    case 'not-interested':
      return (
        <div className="space-y-2">
          <div className={bubbleHer}>{t('Non merci, je ne suis plus intéressée')}</div>
          <div className={bubbleUs}>{t('Bien reçu. Une personne de notre équipe vous confirme l’annulation.')}</div>
          <div className="flex justify-center pt-1">
            <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-gray-700">🔇 {t('Relances arrêtées')}</span>
          </div>
        </div>
      );
    case 'photo-gap': {
      const page = (gap: boolean) => (
        <div className={`grid grid-cols-2 ${gap ? 'gap-1 p-1' : 'gap-0'} bg-white border border-gray-200 rounded-md w-24 h-32 overflow-hidden shadow-sm`}>
          {['#C9A24A', '#7C9A92', '#B5775E', '#5E6E8C'].map((c) => <div key={c} style={{ background: c }} className="rounded-[1px]" />)}
        </div>
      );
      return (
        <div className="flex items-end justify-center gap-6">
          <div className="text-center space-y-1">{page(false)}<p className="text-[10.5px] text-gray-400">{t('Avant (PDF)')}</p></div>
          <span className="text-gray-400 pb-14">→</span>
          <div className="text-center space-y-1">{page(true)}<p className="text-[10.5px] text-gray-600 font-semibold">{t('Maintenant')}</p></div>
        </div>
      );
    }
    case 'whatsapp-line':
      return (
        <div className="space-y-2.5">
          <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-2">
            <span className="text-[12px] font-semibold text-gray-800">omar · 212620066907</span>
            <span className="rounded-full bg-success/10 text-[#56654C] dark:text-status-done px-2 py-0.5 text-[11px] font-semibold">✓ {t('connecté')}</span>
          </div>
          <p className="text-[11px] text-gray-500 text-center">{t('Une panne ? Redémarrage automatique, sans QR.')}</p>
          <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm">
            <p className="text-[10.5px] text-gray-400">{t('Notification · Storiz Admin')}</p>
            <p className="text-[12px] font-semibold text-gray-800">{t('WhatsApp bloqué — rien ne part vers les clientes')}</p>
          </div>
        </div>
      );

    // ─── 10 octobre, soir ───────────────────────────────────────────────────
    case 'live-map': {
      const chip = (name: string, tone: 'blue' | 'red' | 'amber' | 'plain', time?: string) => (
        <div className={`flex items-center gap-1 rounded-md border px-1.5 py-1 text-[10.5px] leading-none ${
          tone === 'red' ? 'border-status-stuck-border bg-status-stuck-bg text-status-stuck font-semibold'
            : tone === 'amber' ? 'border-status-waiting-border bg-status-waiting-bg text-status-waiting font-semibold'
              : tone === 'blue' ? 'border-info/45 bg-info/10 text-info jm-lit' : 'border-gray-200 bg-white text-gray-600'}`}>
          <span className="truncate flex-1">{name}</span>{time && <span className="tabular-nums">{time}</span>}
        </div>
      );
      const cols: Array<[string, ReactNode[]]> = [
        [t('Écrit'), [chip('Sara', 'plain'), chip('Nadia', 'plain')]],
        [t('Photos'), [chip('Hiba', 'red', '3 h'), chip('Imane', 'plain')]],
        [t('Aperçu'), [chip('Salma', 'blue'), chip('Rania', 'amber', '40 min')]],
        [t('Payé'), [chip('Aida', 'plain')]],
      ];
      return (
        <div className="space-y-2.5">
          <div className="flex items-center gap-2 text-[11.5px] text-gray-700">
            <span aria-hidden className="h-2 w-2 rounded-full bg-info jm-pulse text-info" />{t('L’agent écrit à')} <b className="text-info">Salma</b>…
          </div>
          <div className="grid grid-cols-4 gap-1.5">
            {cols.map(([label, chips]) => (
              <div key={label} className="rounded-lg border border-gray-200 bg-white p-1.5 space-y-1">
                <p className="text-[10px] font-semibold text-gray-500 text-center">{label}</p>
                {chips.map((c, n) => <div key={n}>{c}</div>)}
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-3 text-[10.5px] text-gray-500">
            <span><span className="text-status-stuck">●</span> {t('bloquée chez nous')}</span>
            <span><span className="text-status-waiting">●</span> {t('attend votre décision')}</span>
          </div>
        </div>
      );
    }
    case 'delivery-board': {
      const num = (label: string, value: string, tone = 'border-gray-200 bg-white text-gray-800') => (
        <div className={`rounded-lg border px-2 py-1.5 ${tone}`}>
          <p className="text-[9.5px] opacity-80 leading-tight">{label}</p>
          <p className="text-[15px] font-semibold tabular-nums leading-tight">{value}</p>
        </div>
      );
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-4 gap-1.5">
            {num(t('En route'), '7')}
            {num(t('Livrés · 7 j'), '12')}
            {num(t('Problème'), '3', 'border-status-stuck-border bg-status-stuck-bg text-status-stuck')}
            {num(t('AMEEX te doit'), '1 337', 'border-status-done-border bg-status-done-bg text-status-done')}
          </div>
          <div className="grid grid-cols-[1fr_1.2fr] gap-2 items-center">
            <RouteMap route={['kenitra', 'casablanca', 'marrakech']} destination="marrakech" at="casablanca" tone="moving" moving delivered={false} />
            <div className="space-y-1">
              <div className="rounded-md border border-status-stuck-border bg-status-stuck-bg px-2 py-1 text-[10.5px] font-semibold text-status-stuck">● Hiba · Casablanca <span className="float-right">30 h</span></div>
              <div className="rounded-md border border-gray-200 bg-white px-2 py-1 text-[10.5px] text-gray-700">Salma · Marrakech</div>
              <p className="text-[10px] text-gray-500 leading-snug">{t('Le livreur n’a pas pu la joindre 2×. L’agent lui a écrit sur WhatsApp à 11:05 — pas encore de réponse.')}</p>
            </div>
          </div>
        </div>
      );
    }
    case 'ai-captions':
      return (
        <div className="flex justify-center">
          <div className="relative w-40 h-52 rounded-md overflow-hidden shadow-sm border border-gray-200"
            style={{ background: 'linear-gradient(160deg, #F6C28B 0%, #E07A5F 45%, #3D405B 100%)' }}>
            <div aria-hidden className="absolute left-1/2 top-[30%] -translate-x-1/2 h-12 w-12 rounded-full bg-white/35" />
            <div className="absolute inset-x-3 bottom-4 rounded-md px-2 py-1.5 text-center" style={{ background: 'rgba(244, 200, 200, 0.88)', color: '#5B2F3F' }}>
              <p className="text-[10.5px] font-semibold leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>{t('Ton sourire, notre plus beau souvenir ✨')}</p>
              <p className="text-[9px] italic leading-snug opacity-80 mt-0.5">{t('« Le bonheur n’est réel que lorsqu’il est partagé. »')}</p>
            </div>
          </div>
        </div>
      );
    case 'reply-alert':
      return (
        <div className="space-y-2">
          <div className={`${bubbleHer} whitespace-pre-line`}>{'🙋 Hanan — attend une réponse\n« Je ne sais pas comment envoyer les photos »'}</div>
          <div className={`${bubbleUs} w-fit`}>dkhli l + w khtari document</div>
          <p className="text-center text-[10.5px] text-gray-400">↓ {t('remis au propre, dans sa langue')}</p>
          <div className={bubbleHer}>{t('Cliquez sur le bouton (+), puis sur « Document », et choisissez vos photos 😊')}</div>
          <p className="text-[10.5px] text-gray-500 text-right">📤 {t('part dans 1 min · STOP pour annuler')}</p>
        </div>
      );
    // ─── 14 – 15 octobre ────────────────────────────────────────────────────
    case 'print-gate': {
      const step = (label: string, state: 'done' | 'blocked' | 'todo') => (
        <div className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[12px] ${
          state === 'blocked' ? 'border-status-stuck-border bg-status-stuck-bg text-status-stuck font-semibold'
            : state === 'done' ? 'border-gray-200 bg-white text-gray-800' : 'border-dashed border-gray-300 bg-white text-gray-400'}`}>
          <span aria-hidden className="w-4 text-center">{state === 'done' ? '✓' : state === 'blocked' ? '🔒' : '·'}</span>
          <span className="flex-1">{label}</span>
        </div>
      );
      return (
        <div className="space-y-1.5">
          {step(t('Aperçu validé par la cliente'), 'done')}
          {step(t('Avance reçue — 0 / 100 DH'), 'blocked')}
          {step(t('Prêt à imprimer'), 'todo')}
          {step(t('Imprimé'), 'todo')}
          <p className="pt-1 text-[11px] text-gray-500 text-center">{t('Forcer quand même : admin + raison, notée sur la commande')}</p>
        </div>
      );
    }
    case 'complaint':
      return (
        <div className="space-y-2">
          <div className={bubbleHer}>{t('Les photos sont floues, je ne suis pas contente 😞')}</div>
          <div className={bubbleUs}>{t('Je suis vraiment désolée. Une personne de l’équipe s’en occupe tout de suite.')}</div>
          <div className="flex items-center justify-between gap-2 rounded-xl border border-status-stuck-border bg-status-stuck-bg px-3 py-2">
            <span className="text-[11.5px] font-semibold text-status-stuck">⏸ {t('Plainte ouverte — l’agent est en pause')}</span>
            <span className={primaryBtn}>✓ {t('Plainte résolue')}</span>
          </div>
          <p className="text-[10.5px] text-gray-400 text-center">{t('rappel toutes les 48 h tant qu’elle est ouverte')}</p>
        </div>
      );
    case 'send-status': {
      const line = (icon: string, label: string, tone: string) => (
        <div className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[12px] ${tone}`}>
          <span aria-hidden className="w-4 text-center">{icon}</span><span className="flex-1">{label}</span>
        </div>
      );
      return (
        <div className="space-y-1.5">
          <div className="flex justify-center pb-1"><span className={primaryBtn}>✓ {t('Approuver et envoyer')}</span></div>
          {line('📤', t('Part maintenant'), 'border-gray-200 bg-white text-gray-800')}
          {line('🌙', t('Part à 08:00 — heure calme'), 'border-status-waiting-border bg-status-waiting-bg text-status-waiting font-semibold')}
          {line('⏳', t('Attend que les pages 3D soient prêtes'), 'border-gray-200 bg-white text-gray-800')}
          {line('⛔', t('Bloqué — avec la raison'), 'border-status-stuck-border bg-status-stuck-bg text-status-stuck font-semibold')}
        </div>
      );
    }
    case 'server-regen':
      return (
        <div className="flex items-center justify-center gap-3">
          <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-center">
            <p className="text-[18px]" aria-hidden>🖱️</p>
            <p className="text-[11px] font-semibold text-gray-800">{t('Régénérer')}</p>
          </div>
          <span className="text-gray-400">→</span>
          <div className="rounded-lg border border-info/45 bg-info/10 px-3 py-2 text-center">
            <p className="text-[18px]" aria-hidden>🖥️</p>
            <p className="text-[11px] font-semibold text-info">{t('Le serveur')}</p>
            <p className="text-[10px] text-gray-500">{t('démarre en < 1 min')}</p>
          </div>
          <span className="text-gray-400">→</span>
          <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-center">
            <p className="text-[18px]" aria-hidden>📕</p>
            <p className="text-[11px] font-semibold text-gray-800">{t('PDF + 3D')}</p>
            <p className="text-[10px] text-gray-500">{t('onglet fermé : OK')}</p>
          </div>
        </div>
      );
    case 'fixes-1015':
      return (
        <ul className="space-y-1.5">
          {[
            ['🏷️', t('Une étiquette AMEEX = une page, sans page blanche')],
            ['🕑', t('« Version précédente » ne reste plus affichée pour rien')],
            ['⌫', t('Supprimer efface l’élément choisi dans l’éditeur')],
            ['📖', t('Les livres à thème s’ouvrent comme les autres')],
          ].map(([icon, label]) => (
            <li key={label} className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[12px] text-gray-800">
              <span aria-hidden className="w-5 text-center">{icon}</span>
              <span className="flex-1">{label}</span>
              <span className="text-[#56654C] dark:text-status-done font-semibold">✓</span>
            </li>
          ))}
        </ul>
      );
    case 'cover-text':
      return (
        <div className="flex flex-col items-center gap-2">
          <div className="relative w-[150px] aspect-[2/3] rounded-md bg-[#1B3A6B] shadow-inner overflow-hidden">
            <div className="absolute inset-[7%] border border-dashed border-white/60 rounded-sm" />
            <div className="absolute left-[14%] right-[14%] top-[30%] bottom-[34%] rounded-sm bg-black/35 ring-1 ring-brand-400 flex items-center justify-center px-2">
              <p className="text-white text-[10px] leading-tight italic text-center">{t('Tout a commencé quelque part…')}</p>
              {['-left-1 -top-1', '-right-1 -top-1', '-left-1 -bottom-1', '-right-1 -bottom-1'].map((pos) => (
                <span key={pos} className={`absolute ${pos} w-2 h-2 rounded-full bg-white ring-1 ring-brand-500`} />
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2 py-1 text-[12px] text-gray-800">
            <span className="px-1.5 rounded bg-gray-100">A−</span><span className="font-semibold">18 pt</span><span className="px-1.5 rounded bg-gray-100">A+</span>
            <span className="mx-1 text-gray-300">|</span><span className="italic">Lora</span>
            <span className="mx-1 text-gray-300">|</span><span>{t('Fond')}</span>
          </div>
        </div>
      );
    case 'agent-react':
      return (
        <div className="space-y-2">
          <div className="relative w-fit"><div className={bubbleHer}>Merci bzaf 🙏</div><span className="absolute -bottom-2 left-2 rounded-full bg-white border border-gray-200 px-1 text-[11px]">❤️</span></div>
          <div className={`${bubbleHer} mt-3`}>Je veux commander : Duo — 749 DH</div>
          <div className={bubbleUs}>{t('Merci pour votre commande ! 🤍\n2 albums · 36 pages chacun\n💰 Total 749 DH\nC’est pour quelle occasion ?')}</div>
        </div>
      );
    case 'agent-actions':
      return (
        <ul className="space-y-1.5">
          {[
            ['🔗', t('Lien de l’éditeur envoyé'), 'undo'],
            ['📖', t('Page de fin retirée'), 'undo'],
            ['💸', t('Remise 50 DH — attend votre OK'), 'ok'],
          ].map(([icon, label, kind]) => (
            <li key={label} className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[12px] text-gray-800">
              <span aria-hidden className="w-5 text-center">{icon}</span>
              <span className="flex-1">{label}</span>
              {kind === 'undo'
                ? <span className="rounded-md border border-gray-300 px-2 py-0.5 text-[11px]">{t('Annuler')}</span>
                : <span className="flex gap-1"><span className="rounded-md bg-brand-500 text-white px-2 py-0.5 text-[11px]">{t('Valider')}</span><span className="rounded-md border border-gray-300 px-2 py-0.5 text-[11px]">{t('Refuser')}</span></span>}
            </li>
          ))}
        </ul>
      );
    case 'playbooks':
      return (
        <div className="rounded-lg border border-gray-200 bg-white p-3 text-[12px] text-gray-800 space-y-1.5">
          <p><span className="font-semibold">{t('Situation')} :</span> {t('la cliente trouve ça cher')}</p>
          <p><span className="font-semibold">{t('Réponse')} :</span> {t('comprendre, rappeler la qualité, proposer le Solo')}</p>
          <div className="flex gap-1.5 pt-1">
            <span className="rounded-md bg-brand-500 text-white px-2 py-0.5 text-[11px]">{t('Valider')}</span>
            <span className="rounded-md border border-gray-300 px-2 py-0.5 text-[11px]">{t('Modifier')}</span>
            <span className="rounded-md border border-gray-300 px-2 py-0.5 text-[11px]">{t('Refuser')}</span>
          </div>
        </div>
      );
    case 'fixes-1030':
      return (
        <ul className="space-y-1.5">
          {[
            ['🎨', t('La couleur de tranche choisie reste choisie')],
            ['📖', t('L’aperçu 3D rapide montre vos derniers changements')],
            ['🗑️', t('Supprimer un élément de couverture marche')],
            ['🎙️', t('Les vocaux sont compris du premier coup')],
            ['💬', t('Plus de silence après une retouche à la main')],
          ].map(([icon, label]) => (
            <li key={label} className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[12px] text-gray-800">
              <span aria-hidden className="w-5 text-center">{icon}</span>
              <span className="flex-1">{label}</span>
              <span className="text-[#56654C] dark:text-status-done font-semibold">✓</span>
            </li>
          ))}
        </ul>
      );
    case 'fixes':
      return (
        <ul className="space-y-1.5">
          {[
            ['🎙️', t('Vocaux et vidéos lus jusqu’au bout')],
            ['✉️', t('Plus aucun message envoyé 2 fois')],
            ['🖨️', t('« Régénérer » ne bloque plus sur une photo mal mesurée')],
            ['✕', t('Fermez une alerte système de l’Inbox')],
            ['🪟', t('Le texte de l’intro ne passe plus sur les fenêtres')],
          ].map(([icon, label]) => (
            <li key={label} className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[12px] text-gray-800">
              <span aria-hidden className="w-5 text-center">{icon}</span>
              <span className="flex-1">{label}</span>
              <span className="text-[#56654C] dark:text-status-done font-semibold">✓</span>
            </li>
          ))}
        </ul>
      );
  }
}

export function WhatsNew({ release, onClose }: { release: PatchRelease; onClose: () => void }) {
  const { t } = useT();
  const [i, setI] = useState(0);
  const item = release.items[i];
  const last = i === release.items.length - 1;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (!item) return null;
  return (
    <div className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="whatsnew-title">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg max-h-[92vh] sm:min-h-[600px] overflow-auto bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 pt-4">
          <p className="text-[12px] font-semibold text-brand-600 dark:text-brand-300">✨ {t(release.title)} · {release.date}</p>
          <button type="button" onClick={onClose} aria-label={t('Fermer')}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" /></svg>
          </button>
        </div>

        <div className="px-5 pb-5 pt-2 flex-1 flex flex-col">
          <h2 id="whatsnew-title" className="text-[18px] font-semibold text-gray-900 leading-snug">{t(item.title)}</h2>
          {item.badge && (
            <span className="self-start inline-block mt-1.5 rounded-full border border-amber-300 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:text-amber-200">{t(item.badge)}</span>
          )}

          <div className="mt-4 rounded-xl border border-gray-100 bg-gray-50 p-4">
            {item.images?.length ? (
              <div className="space-y-2">
                {item.images.map((src) => <img key={src} src={src} alt={t(item.title)} className="block mx-auto max-w-full rounded-lg border border-gray-200" />)}
              </div>
            ) : item.image
              ? <img src={item.image} alt={t(item.title)} className="block mx-auto max-w-full max-h-[340px] object-contain rounded-lg border border-gray-200" />
              : item.visual ? <Visual kind={item.visual} /> : null}
          </div>

          <p className="mt-4 text-[14px] leading-relaxed text-gray-700">{t(item.what)}</p>

          {item.how.length > 0 ? (
            <ol className="mt-3 space-y-1.5">
              {item.how.map((step, n) => (
                <li key={step} className="flex gap-2.5 text-[13.5px] leading-snug text-gray-800">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-brand-500 text-white text-[11px] font-semibold flex items-center justify-center">{n + 1}</span>
                  <span>{t(step)}</span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="mt-3 text-[13px] text-[#56654C] dark:text-status-done font-semibold">✓ {t('Rien à faire — ça marche tout seul.')}</p>
          )}

          <div className="mt-auto sticky bottom-0 -mx-5 px-5 pt-4 pb-[max(env(safe-area-inset-bottom),4px)] bg-white flex items-center justify-between gap-3">
            <div className="flex gap-1.5" aria-label={t('{n} sur {total}', { n: String(i + 1), total: String(release.items.length) })}>
              {release.items.map((it, n) => (
                <button key={it.title} type="button" onClick={() => setI(n)} aria-label={t(it.title)}
                  className={`h-2 rounded-full transition-all duration-200 ${n === i ? 'w-5 bg-brand-500' : 'w-2 bg-gray-300 hover:bg-gray-400'}`} />
              ))}
            </div>
            <div className="flex gap-2">
              {i > 0 && (
                <button type="button" onClick={() => setI(i - 1)}
                  className="h-10 px-3.5 rounded-lg border border-gray-200 text-[13px] font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
                  {t('Retour')}
                </button>
              )}
              <button type="button" onClick={() => (last ? onClose() : setI(i + 1))}
                className="h-10 px-4 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-[13px] font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-500">
                {last ? t('J’ai compris') : t('Suivant')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Shows the unseen release once; `reopen` (sidebar link) shows the latest again. */
export function useWhatsNew() {
  const [open, setOpen] = useState<PatchRelease | null>(() => unseenRelease());
  const close = () => { if (open) markPatchSeen(open.id); setOpen(null); };
  const reopen = () => { if (PATCH_NOTES[0]) setOpen(PATCH_NOTES[0]); };
  return { open, close, reopen };
}
